import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/components/Button';
import { IconWithBackground } from '../ui/components/IconWithBackground';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useHomePageData } from '../hooks/useHomePageData';
import { FeatherImage, FeatherChevronDown, FeatherChevronUp } from '@subframe/core';

// Helper function to format numbers
const formatNumber = (num: number | string): string => {
  const value = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(value)) return '0';

  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toFixed(0);
};

// Unified home page for all users (authenticated and visitors)
export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useHomePageData();
  const [showMoreVolume, setShowMoreVolume] = useState(false);
  const [showMoreTrending, setShowMoreTrending] = useState(false);

  const handleCollectionClick = (collectionId: string, slug?: string) => {
    // Use slug if present, otherwise fall back to ID
    const identifier = slug || collectionId;
    navigate(`/collections/${identifier}`);
  };

  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center px-6 py-24 gap-4">
          <LoadingSpinner size="lg" className="text-brand-primary" />
        </div>
      </DefaultPageLayout>
    );
  }

  if (isError) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center px-6 py-12">
          <div className="text-heading-2 font-heading-2 text-error-600 mb-4">Error loading data</div>
          <div className="text-body font-body text-subtext-color">Please try refreshing the page</div>
        </div>
      </DefaultPageLayout>
    );
  }

  const { featuredCollection, topVolume, trending, topGainers, topLosers } = data;

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 mobile:gap-4">
        <span className="text-heading-1 font-heading-1 text-default-font mobile:text-heading-2 mobile:font-heading-2">
          XRP Deals
        </span>

        {/* Featured Collection Card */}
        {featuredCollection && (
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-brand-primary bg-brand-50 px-6 py-6 mobile:px-4 mobile:py-4 mobile:gap-3">
            <div className="flex w-full items-center gap-3">
              <span className="text-caption-bold font-caption-bold text-brand-primary">
                FEATURED COLLECTION
              </span>
            </div>
            <div className="flex w-full items-center gap-4 mobile:flex-col mobile:items-start mobile:gap-3">
              <div className="flex items-center gap-4 mobile:w-full">
                <div style={{ width: '80px', height: '80px', flexShrink: 0, overflow: 'hidden', borderRadius: '8px' }} className="mobile:w-16 mobile:h-16">
                  {featuredCollection.imageUrl ? (
                    <img
                      src={featuredCollection.imageUrl}
                      alt={featuredCollection.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  ) : (
                    <IconWithBackground
                      size="large"
                      icon={<FeatherImage />}
                      square={true}
                    />
                  )}
                </div>
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font mobile:text-heading-3 mobile:font-heading-3">
                    {featuredCollection.name}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4 mobile:w-full mobile:justify-between">
                <div className="flex flex-col items-end gap-1 mobile:items-start">
                  <span className="text-caption font-caption text-subtext-color">
                    Floor Price
                  </span>
                  <span className="text-heading-3 font-heading-3 text-brand-primary mobile:text-body-bold mobile:font-body-bold">
                    {featuredCollection.floorPrice ? `${formatNumber(featuredCollection.floorPrice)} XRP` : 'N/A'}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1 mobile:items-start">
                  <span className="text-caption font-caption text-subtext-color">
                    24h Volume
                  </span>
                  <span className="text-heading-3 font-heading-3 text-brand-primary mobile:text-body-bold mobile:font-body-bold">
                    {formatNumber(featuredCollection.volume24h)} XRP
                  </span>
                </div>
              </div>
              <Button onClick={() => handleCollectionClick(featuredCollection.collectionId, featuredCollection.slug)} className="mobile:w-full">
                View Collection
              </Button>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid w-full grid-cols-1 md:grid-cols-2 gap-4">
          {/* Top Volume Card */}
          <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Top Volume
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Highest volume in 24h
              </span>
            </div>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              {topVolume
                .slice(0, showMoreVolume ? 10 : 5)
                .map((collection, idx) => (
                <div
                  key={collection.collectionId}
                  className="flex w-full items-center gap-3 cursor-pointer hover:opacity-80"
                  onClick={() => handleCollectionClick(collection.collectionId, collection.slug)}
                >
                  <span className="text-body-bold font-body-bold text-subtext-color">
                    {idx + 1}
                  </span>
                  <div style={{ width: '32px', height: '32px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px' }}>
                    {collection.imageUrl ? (
                      <img
                        src={collection.imageUrl}
                        alt={collection.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <IconWithBackground
                        size="small"
                        icon={<FeatherImage />}
                      />
                    )}
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start">
                    <span className="line-clamp-1 w-full text-body-bold font-body-bold text-default-font">
                      {collection.name}
                    </span>
                  </div>
                  <span className="text-body-bold font-body-bold text-brand-primary">
                    {formatNumber(collection.volume24h)} XRP
                  </span>
                </div>
              ))}
              {topVolume.length > 5 && (
                <button
                  onClick={() => setShowMoreVolume(!showMoreVolume)}
                  className="flex w-full items-center justify-center gap-1 text-caption font-caption text-brand-primary hover:text-brand-600 transition-colors pt-1"
                >
                  {showMoreVolume ? (
                    <>Show less <FeatherChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Show more <FeatherChevronDown className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Trending Card */}
          <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Trending
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Most sales in 24h
              </span>
            </div>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              {trending
                .slice(0, showMoreTrending ? 10 : 5)
                .map((collection, idx) => (
                <div
                  key={collection.collectionId}
                  className="flex w-full items-center gap-3 cursor-pointer hover:opacity-80"
                  onClick={() => handleCollectionClick(collection.collectionId, collection.slug)}
                >
                  <span className="text-body-bold font-body-bold text-subtext-color">
                    {idx + 1}
                  </span>
                  <div style={{ width: '32px', height: '32px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px' }}>
                    {collection.imageUrl ? (
                      <img
                        src={collection.imageUrl}
                        alt={collection.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <IconWithBackground
                        variant={idx === 0 ? "brand" : idx === 1 ? "success" : idx === 2 ? "warning" : idx === 3 ? "error" : "neutral"}
                        size="small"
                        icon={<FeatherImage />}
                      />
                    )}
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start">
                    <span className="line-clamp-1 w-full text-body-bold font-body-bold text-default-font">
                      {collection.name}
                    </span>
                  </div>
                  <span className="text-body-bold font-body-bold text-brand-primary">
                    {collection.currentSales} sales
                  </span>
                </div>
              ))}
              {trending.length > 5 && (
                <button
                  onClick={() => setShowMoreTrending(!showMoreTrending)}
                  className="flex w-full items-center justify-center gap-1 text-caption font-caption text-brand-primary hover:text-brand-600 transition-colors pt-1"
                >
                  {showMoreTrending ? (
                    <>Show less <FeatherChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Show more <FeatherChevronDown className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Rising Floors Card */}
          <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Rising Floors
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Floor trending up vs 30-day average
              </span>
            </div>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              {topGainers.slice(0, 5).map((collection, idx) => (
                <div
                  key={collection.collectionId}
                  className="flex w-full items-center gap-3 cursor-pointer hover:opacity-80"
                  onClick={() => handleCollectionClick(collection.collectionId, collection.slug)}
                >
                  <span className="text-body-bold font-body-bold text-subtext-color">
                    {idx + 1}
                  </span>
                  <div style={{ width: '32px', height: '32px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px' }}>
                    {collection.imageUrl ? (
                      <img
                        src={collection.imageUrl}
                        alt={collection.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <IconWithBackground
                        variant={idx === 0 ? "brand" : idx === 1 ? "success" : idx === 2 ? "warning" : idx === 3 ? "error" : "neutral"}
                        size="small"
                        icon={<FeatherImage />}
                      />
                    )}
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start">
                    <span className="line-clamp-1 w-full text-body-bold font-body-bold text-default-font">
                      {collection.name}
                    </span>
                  </div>
                  <span className="text-body-bold font-body-bold text-success-600">
                    ↑ {collection.floorPriceChange.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Falling Floors Card */}
          <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Falling Floors
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Floor trending down vs 30-day average
              </span>
            </div>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              {topLosers.slice(0, 5).map((collection, idx) => (
                <div
                  key={collection.collectionId}
                  className="flex w-full items-center gap-3 cursor-pointer hover:opacity-80"
                  onClick={() => handleCollectionClick(collection.collectionId, collection.slug)}
                >
                  <span className="text-body-bold font-body-bold text-subtext-color">
                    {idx + 1}
                  </span>
                  <div style={{ width: '32px', height: '32px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px' }}>
                    {collection.imageUrl ? (
                      <img
                        src={collection.imageUrl}
                        alt={collection.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <IconWithBackground
                        variant={idx === 0 ? "brand" : idx === 1 ? "success" : idx === 2 ? "warning" : idx === 3 ? "error" : "neutral"}
                        size="small"
                        icon={<FeatherImage />}
                      />
                    )}
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start">
                    <span className="line-clamp-1 w-full text-body-bold font-body-bold text-default-font">
                      {collection.name}
                    </span>
                  </div>
                  <span className="text-body-bold font-body-bold text-error-600">
                    ↓ {Math.abs(collection.floorPriceChange).toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
